import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "Who is behind AfterBurn? Are you anonymous?",
    a: "No — the founder is fully doxxed. AfterBurn is led by Bear, a member of Nipissing First Nation. Wildfire recovery isn't abstract here; it's about our land and our communities. A doxxed founder is the opposite of a rug pull.",
  },
  {
    q: "How do I know this isn't a scam or a rug pull?",
    a: "Three things a scammer would never do: (1) the founder is public and doxxed, (2) liquidity is locked for 365 days through PinkSale, and (3) the contract is verified and the Charity, Operations, and Liquidity wallets are public on BaseScan. Don't trust us — verify all of it yourself.",
  },
  {
    q: "How can I verify that donations actually happen?",
    a: "Every donation is proven two ways: the on-chain transaction anyone can verify on BaseScan (funds leaving the charity wallet), plus the official receipt from the receiving organization (funds arriving). Both are published on our public Transparency page. Prove it, don't promise it.",
  },
  {
    q: "What is the transaction tax and where does it go?",
    a: "A 7% tax is applied to every trade and automatically split by the contract: 3% to the Wildfire Relief Fund (charity), 3% to core operations and maintenance, and 1% to automated liquidity. No middleman touches it — the contract enforces the split.",
  },
  {
    q: "What does the charity portion actually fund?",
    a: "The 3% Wildfire Relief Fund goes toward the recovery work that lasts: reforestation and replanting native forests, rebuilding communities that lost everything, and supporting the first responders on the front lines.",
  },
  {
    q: "Are you officially partnered with any charities?",
    a: "Not yet, and we won't pretend otherwise. Funds accumulate in a public charity wallet you can watch live on BaseScan. When we donate, it goes to established, verified wildfire-recovery and reforestation organizations — and every distribution is published with on-chain proof and a receipt.",
  },
  {
    q: "Where does the presale happen, and is it fair?",
    a: "The presale runs on PinkSale, on the Base network. It's a fair public sale — no whitelist games, no insiders, no early access. Everyone buys at the same price at the same time. The exact date is announced across our official channels and on this site.",
  },
  {
    q: "How do I buy, and what do I need?",
    a: "You'll need a wallet (like MetaMask) funded with ETH on the Base network — Base mainnet, not Ethereum mainnet. During presale, buy through PinkSale. After the DEX listing, you can buy any amount on Uniswap (Base).",
  },
  {
    q: "What are the minimum and maximum buy amounts?",
    a: "In the presale, the minimum buy is 0.05 ETH and the maximum is 1 ETH per wallet. The cap keeps things fair — no single buyer can dominate the sale, so more of the community gets a share. After the DEX listing you can buy any amount on Uniswap (Base), with no per-wallet limit.",
  },
  {
    q: "What are the soft cap and hard cap?",
    a: "Soft cap is 10 ETH — the minimum the presale must raise to succeed and finalize. Hard cap is 20 ETH — the maximum it will raise, after which the sale closes. If it lands between the two, it finalizes and lists; if it never reaches 10 ETH, everyone is refunded.",
  },
  {
    q: "What happens if the presale doesn't reach its soft cap?",
    a: "If the presale doesn't hit its soft cap (10 ETH) by the time it ends, the sale simply doesn't finalize — and every contributor can claim a full refund of their ETH directly from the PinkSale contract. Because it runs on PinkSale's audited launchpad, refunds are automatic and trustless: the team never takes custody of your funds during the sale, so there's nothing for us to withhold. You either get your tokens, or you get your ETH back.",
  },
  {
    q: "When does it list on a DEX, and at what price?",
    a: "Shortly after the presale finalizes, the contract auto-creates the liquidity pool and $AFTBRN lists on Uniswap (Base). The listing price is set about 25% above the presale price — so presale buyers start ahead of anyone buying at listing.",
  },
  {
    q: "Can I sell whenever I want after launch?",
    a: "Yes. Your tokens are yours — there's no lock on holders. Once trading goes live on Uniswap, you can buy or sell any amount, any time. The only thing that applies is the 7% trade tax, which funds the mission and operations on every buy and sell.",
  },
  {
    q: "Is the smart contract audited?",
    a: "The contract is verified and fully public on BaseScan, so anyone can read the code directly. A third-party security audit is part of our roadmap around launch. We'll publish the results openly when it's complete — transparency applies to the code, not just the wallets.",
  },
  {
    q: "Is the liquidity locked?",
    a: "Yes — liquidity is locked for 365 days via PinkSale's locker, and the lock is public and verifiable. Locking liquidity is the exact opposite of a rug pull: it means the pool can't be pulled, even by the founder.",
  },
  {
    q: "Is any of this financial advice?",
    a: "No. AfterBurn is a mission-first project, not an investment promise. Nothing here is financial, investment, legal, or tax advice. Only participate with what you understand and can afford, and always do your own research.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" className="py-24 md:py-32 px-6 md:px-12 max-w-4xl mx-auto" data-testid="faq-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl mb-14"
      >
        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ember">
          <ShieldCheck className="w-4 h-4" /> FAQ
        </span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
          Straight answers. Nothing hidden.
        </h2>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">
          The honest questions people ask about trust, transparency, and how AfterBurn works.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-base md:text-lg font-display font-semibold py-5 hover:no-underline hover:text-ember">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </section>
  );
}
